import React, { FC, HTMLAttributes, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'linaria/react';
import { defaultTheme, withTheme } from '@shared/lib/theme';
import { useBreakpoint, usePortalById } from '@shared/lib/react';
import { Text } from '@shared/ui/atoms';
import { tooltipPositions, getContentTransform, getArrowTop, getArrowBottom, getArrowLeft, getArrowRight, getArrowMarginTop, getArrowMarginLeft } from '@shared/config/components/tooltip';
import { BottomSheet, BottomSheetContent } from '@shared/ui/molecules';
export type TooltipPosition = keyof typeof tooltipPositions;
export interface TooltipProps extends Omit<HTMLAttributes<HTMLElement>, 'content' | 'title'> {
  title?: ReactNode;
  content: ReactNode;
  position?: TooltipPosition;
  delay?: number;
  disabled?: boolean;
  hasArrow?: boolean;
  color?: keyof typeof defaultTheme.colors.background;
  maxWidth?: string;
}
interface TooltipCoordinates {
  top: number;
  left: number;
}
const Tooltip: FC<TooltipProps> = ({
  title,
  content,
  position = 'top',
  delay = 200,
  disabled = false,
  hasArrow = true,
  color = 'inverted',
  maxWidth = '16.25rem',
  children,
  ...rest
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const [coordinates, setCoordinates] = useState<TooltipCoordinates>({
    top: 0,
    left: 0
  });
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const modalContainer = usePortalById('modal-container');
  const {
    isMobile
  } = useBreakpoint();
  const updateCoordinates = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    let top = 0;
    let left = 0;
    switch (position) {
      case 'top':
        top = rect.top - gap;
        left = rect.left + rect.width / 2;
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - gap;
        break;
      default:
        top = rect.top + rect.height / 2;
        left = rect.right + gap;
        break;
    }
    setCoordinates({
      top,
      left
    });
  };
  const handleMouseEnter = () => {
    if (disabled || isMobile) return;
    const id = setTimeout(() => {
      updateCoordinates();
      setIsTooltipOpen(true);
    }, delay);
    setTimeoutId(id);
  };
  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsTooltipOpen(false);
  };
  const handleBottomSheetOpen = () => {
    setIsTooltipOpen(true);
  };
  const handleBottomSheetClose = () => {
    setIsTooltipOpen(false);
  };
  if (!modalContainer) return null;
  return <>
      <TooltipContainer {...rest}>
        <TooltipTrigger ref={triggerRef} onClick={handleBottomSheetOpen} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {children}
        </TooltipTrigger>
      </TooltipContainer>

      {isTooltipOpen && createPortal(<>
            {isMobile ? <BottomSheet open={isTooltipOpen} onClose={handleBottomSheetClose}>
                <BottomSheetContent title={title} onClose={handleBottomSheetClose}>
                  <Text family="MTSCompact" size="pS" weight="medium" color="primary">
                    {content}
                  </Text>
                </BottomSheetContent>
              </BottomSheet> : <TooltipContent position={position} color={color} maxWidth={maxWidth} style={{
        top: coordinates.top,
        left: coordinates.left
      }}>
                {hasArrow && <TooltipArrow position={position} color={color} />}
                {typeof content === 'string' ? <Text family="MTSCompact" size="pS" weight="medium" color="inverted">
                    {content}
                  </Text> : content}
              </TooltipContent>}
          </>, modalContainer as Element)}
    </>;
};
export default Tooltip;
const TooltipContainer = /*#__PURE__*/styled('div')({
  name: "TooltipContainer",
  class: "t54fp44",
  propsAsIs: false
});
const TooltipTrigger = /*#__PURE__*/styled('div')({
  name: "TooltipTrigger",
  class: "t11sqdsj",
  propsAsIs: false
});
type TooltipContentProps = Required<Pick<TooltipProps, 'position' | 'color' | 'maxWidth'>>;
const _exp = /*#__PURE__*/() => ({
  color,
  theme
}) => theme.colors.background[color];
const _exp2 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.overlay.primary;
const _exp3 = /*#__PURE__*/() => ({
  maxWidth
}) => maxWidth;
const _exp4 = /*#__PURE__*/() => ({
  position
}) => getContentTransform(position);
const _exp5 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.inverted;
const TooltipContent = withTheme(/*#__PURE__*/styled('div')({
  name: "TooltipContent",
  class: "t1h8h6vn",
  propsAsIs: false,
  vars: {
    "t1h8h6vn-0": [_exp()],
    "t1h8h6vn-1": [_exp2()],
    "t1h8h6vn-2": [_exp3()],
    "t1h8h6vn-3": [_exp4()],
    "t1h8h6vn-4": [_exp5()]
  }
}));
type TooltipArrowProps = Required<Pick<TooltipProps, 'position' | 'color'>>;
const _exp6 = /*#__PURE__*/() => ({
  color,
  theme
}) => theme.colors.background[color];
const _exp7 = /*#__PURE__*/() => ({
  position
}) => getArrowTop(position, '-0.25rem');
const _exp8 = /*#__PURE__*/() => ({
  position
}) => getArrowBottom(position, '-0.25rem');
const _exp9 = /*#__PURE__*/() => ({
  position
}) => getArrowLeft(position, '-0.25rem');
const _exp0 = /*#__PURE__*/() => ({
  position
}) => getArrowRight(position, '-0.25rem');
const _exp1 = /*#__PURE__*/() => ({
  position
}) => getArrowMarginTop(position, '-0.25rem');
const _exp10 = /*#__PURE__*/() => ({
  position
}) => getArrowMarginLeft(position, '-0.25rem');
const _exp11 = /*#__PURE__*/() => ({
  position
}) => getArrowTop(position, '-4px');
const _exp12 = /*#__PURE__*/() => ({
  position
}) => getArrowBottom(position, '-4px');
const _exp13 = /*#__PURE__*/() => ({
  position
}) => getArrowLeft(position, '-4px');
const _exp14 = /*#__PURE__*/() => ({
  position
}) => getArrowRight(position, '-4px');
const _exp15 = /*#__PURE__*/() => ({
  position
}) => getArrowMarginTop(position, '-4px');
const _exp16 = /*#__PURE__*/() => ({
  position
}) => getArrowMarginLeft(position, '-4px');
const TooltipArrow = withTheme(/*#__PURE__*/styled('div')({
  name: "TooltipArrow",
  class: "tq9gm0t",
  propsAsIs: false,
  vars: {
    "tq9gm0t-0": [_exp6()],
    "tq9gm0t-1": [_exp7()],
    "tq9gm0t-2": [_exp8()],
    "tq9gm0t-3": [_exp9()],
    "tq9gm0t-4": [_exp0()],
    "tq9gm0t-5": [_exp1()],
    "tq9gm0t-6": [_exp10()],
    "tq9gm0t-7": [_exp11()],
    "tq9gm0t-8": [_exp12()],
    "tq9gm0t-9": [_exp13()],
    "tq9gm0t-10": [_exp14()],
    "tq9gm0t-11": [_exp15()],
    "tq9gm0t-12": [_exp16()]
  }
}));

require("./Tooltip.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./Tooltip.tsx");